import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { X, Loader2, RotateCcw, Save, Upload, Clock, Bookmark } from "lucide-react";
import { fmtWhen } from "@/components/docs/DocShared";
import { evaluateSheet, colLetter } from "@/lib/sheetFormula";

const KIND = { import: ["Before import", Upload, "text-[#b06a00] bg-[#FFF4E0]"], auto: ["Autosave", Clock, "text-[#595D62] bg-[#F0F0F5]"],
  manual: ["Saved version", Bookmark, "text-[#5b2fd6] bg-[#EDE7FF]"], restore: ["Before restore", RotateCcw, "text-[#2563eb] bg-[#E5EEFF]"] };

function VersionPreview({ v }) {
  const vals = evaluateSheet(v.columns, v.rows).slice(0, 15);
  return (
    <div className="rounded-xl border border-[#EEEFEF] overflow-auto max-h-56 mt-2" data-testid="sheet-version-preview">
      <table className="text-[11px] w-max min-w-full"><thead className="bg-[#FAFAFD]"><tr>{v.columns.map((c, i) => <th key={c.id} className="px-2 py-1 text-left font-semibold border-r border-[#EEEFEF]">{c.name || colLetter(i)}</th>)}</tr></thead>
        <tbody>{vals.map((r, ri) => <tr key={ri} className="border-t border-[#f3f3f6]">{r.map((x, ci) => <td key={ci} className="px-2 py-0.5 text-[#595D62] border-r border-[#f3f3f6] max-w-[140px] truncate">{String(x ?? "")}</td>)}</tr>)}</tbody></table>
    </div>
  );
}

export function SheetHistory({ sheetId, flush, onRestored, onClose, nonce }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/console/sheets/${sheetId}/versions`).then(({ data: d }) => setData(d)).catch(() => setData({ versions: [], can_restore: false }));
  useEffect(() => { load(); }, [sheetId, nonce]); // eslint-disable-line
  const save = async () => {
    setBusy(true);
    try { await flush(); await api.post(`/console/sheets/${sheetId}/versions`, { kind: "manual", label }); setLabel(""); toast.success("Version saved", { position: "bottom-right" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const view = async (v) => {
    if (open?.id === v.id) { setOpen(null); return; }
    const { data: full } = await api.get(`/console/sheets/${sheetId}/versions/${v.id}`); setOpen(full);
  };
  const restore = async (v) => {
    setBusy(true);
    try { await flush(); await api.post(`/console/sheets/${sheetId}/versions/${v.id}/restore`); toast.success("Version restored. The previous state was saved too", { position: "bottom-right" }); setOpen(null); await onRestored(); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <aside className="fixed right-0 top-0 h-full w-full sm:w-[400px] z-40 bg-white border-l border-[#EEEFEF] shadow-[-12px_0_40px_rgba(6,6,12,0.06)] flex flex-col animate-in slide-in-from-right duration-200" data-testid="sheet-history">
      <div className="px-5 py-4 border-b border-[#EEEFEF] flex items-center gap-2">
        <p className="font-display text-lg font-semibold text-[#06060C]">Version history</p>
        <span className="font-mono-data text-[10px] text-[#9FA1A4] uppercase tracking-wider">last 50</span>
        <button data-testid="sheet-history-close" onClick={onClose} className="ml-auto h-8 w-8 rounded-full hover:bg-[#F6F6FD] flex items-center justify-center text-[#9FA1A4]"><X className="h-4 w-4" /></button>
      </div>
      <div className="px-5 py-3 border-b border-[#f3f3f6] flex gap-2">
        <Input data-testid="sheet-version-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Label (optional)" className="h-9 bg-[#F6F6FD] border-[#EEEFEF] text-sm" />
        <Button data-testid="sheet-version-save" onClick={save} disabled={busy} className="h-9 rounded-full bg-[#06060C] text-white hover:bg-[#1c1c24] shrink-0"><Save className="h-3.5 w-3.5 mr-1.5" /> Save version</Button>
      </div>
      {data && !data.can_restore && <p className="px-5 py-2 text-[11px] text-[#9FA1A4] bg-[#FAFAFD]" data-testid="sheet-history-no-restore">Only the sheet's creator or an admin can restore versions.</p>}
      <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2" data-testid="sheet-version-list">
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc]" /></div>
          : data.versions.length === 0 ? <p className="text-sm text-[#9FA1A4] py-6 text-center" data-testid="sheet-history-empty">No versions yet. They're saved before imports, every 10 minutes while editing, and when you click Save version.</p>
          : data.versions.map((v) => {
            const [kl, I, tint] = KIND[v.kind] || KIND.manual;
            return (
              <div key={v.id} data-testid={`sheet-version-${v.id}`} className={`rounded-xl border px-3 py-2.5 transition-colors ${open?.id === v.id ? "border-[#C9B8FF] bg-[#FCFBFF]" : "border-[#EEEFEF]"}`}>
                <button onClick={() => view(v)} className="w-full text-left" data-testid={`sheet-version-view-${v.id}`}>
                  <div className="flex items-center gap-2"><span className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-mono-data uppercase tracking-wider ${tint}`}><I className="h-2.5 w-2.5" />{kl}</span><span className="text-[11px] text-[#9FA1A4] ml-auto">{fmtWhen(v.at)}</span></div>
                  {v.label && v.label !== kl && <p className="text-sm text-[#06060C] mt-1">{v.label}</p>}
                  <p className="text-[11px] text-[#9FA1A4] mt-0.5">{v.by || "—"} · {v.row_count} rows · {v.col_count} columns</p>
                </button>
                {open?.id === v.id && <>
                  <VersionPreview v={open} />
                  {data.can_restore && <Button data-testid={`sheet-version-restore-${v.id}`} disabled={busy} onClick={() => restore(v)} className="mt-2 h-8 rounded-full bg-[#723FFF] text-white hover:bg-[#5b2fd6] text-xs"><RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restore this version</Button>}
                </>}
              </div>
            );
          })}
      </div>
    </aside>
  );
}
